import React from 'react';

import * as SwitchPrimitive from '@radix-ui/react-switch';
import { tv } from 'tailwind-variants';

import cn from '../../utils/cn';

export type SwitchProps = SwitchPrimitive.SwitchProps & {
  size?: 'regular' | 'condensed';
  disabled?: boolean;
};

const switchRootVariants = tv({
  base: 'relative inline-flex rounded-full border-2 border-transparent transition-colors duration-200',
  variants: {
    size: {
      regular: 'h-5 w-9',
      condensed: 'h-4 w-7',
    },
    disabled: {
      true: 'cursor-not-allowed',
      false: 'cursor-pointer',
    },
    checked: {
      true: 'bg-success',
      false: 'bg-gray-dark',
    },
  },
  compoundVariants: [
    {
      checked: true,
      disabled: true,
      className: 'bg-success-light',
    },
    {
      checked: false,
      disabled: true,
      className: 'bg-gray-light',
    },
  ],
});

const switchThumbVariants = tv({
  base: 'flex transform  rounded-full	bg-white transition duration-200',
  variants: {
    size: {
      regular: 'h-4 w-4',
      condensed: 'h-3 w-3',
    },
    disabled: {
      true: 'cursor-not-allowed',
      false: 'cursor-pointer',
    },
    checked: {
      true: 'translate-x-3.5',
      false: 'translate-x-0',
    },
  },
  compoundVariants: [
    {
      size: 'regular',
      checked: true,
      className: 'translate-x-4',
    },
    {
      size: 'condensed',
      checked: true,
      className: 'translate-x-3',
    },
  ],
});

const Switch = React.forwardRef<HTMLDivElement, SwitchProps>(({ size = 'regular', disabled = false, ...props }) => {
  return (
    <SwitchPrimitive.Root
      data-testid='switch'
      className={cn(
        switchRootVariants({
          size,
          disabled,
          checked: props.checked,
        })
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          switchThumbVariants({
            size,
            disabled,
            checked: props.checked,
          })
        )}
      />
    </SwitchPrimitive.Root>
  );
});

Switch.displayName = SwitchPrimitive.Root.displayName;

export default Switch;
